<template>
  <div class="relative mx-auto w-full max-w-3xl">

    <BorderGradient
      v-if="store.resume.file"
      :border-radius="1"
      class="mt-6">
      <div class="flex h-20 items-center justify-start gap-4 rounded-xl px-4 xs:px-6 lg:gap-6 lg:px-10">
        <DocumentIcon class="h-8 w-8 text-bdBlue-600"/>
        <div>
          <p class="font-bold">{{ fileName || store.resume.file?.name }}</p>
          <p class="text-sm text-slate-600">Ready to Upload • {{ formattedFileSize }}</p>
        </div>
        <HoverTooltip tip="Upload a different file" class="ml-auto" placement="left">
          <button type="button" @click="store.reset()">
            <ArrowsRightLeftIcon class="h-8 w-8 text-slate-900 p-1.5"/>
          </button>
        </HoverTooltip>
      </div>
    </BorderGradient>

    <div v-show="!store.resume.file">
      <UploadResume
        :backgroundHex="backgroundHex"
        :foregroundHex="foregroundHex"
        v-model:file="store.resume.file"
        v-model:fileDataUrl="store.resume.fileDataUrl"
        :uploading="store.resume.uploading"
        @update:fileName="() => fileName = $event"
        @update:formattedFileSize="() => formattedFileSize = $event"
        @update:file="focusCheckboxTimeout"
      />
    </div>

    <template v-if="showTermsOfService">

      <FormCheckbox ref="checkboxContainer" v-model="store.resume.tosChecked" class="mx-8 mt-12">
        I agree to the
        <a href="/privacy-policy" target="_blank" tabindex="-1"><u>Privacy Policy</u></a>
        and the
        <a href="/terms-of-service" target="_blank" tabindex="-1"><u>Terms of Service</u></a>
        including that I am over 18 years old and I can legally work in the
        United States.
      </FormCheckbox>

    </template>

    <div class="mt-6 flex w-full items-center justify-center gap-4 px-4">
      <FormButton v-if="hasFile"
                  @click="store.reset"
                  color="secondary"
                  size="tall" class="opacity-70 hover:opacity-100" medium>
        Back
      </FormButton>
      <FormButton @click="store.parseResume(showTermsOfService)"
                  :color="foregroundHex && backgroundHex ? 'hex' : 'gradient'"
                  :hex-foreground="foregroundHex"
                  :hex-background="backgroundHex"
                  size="tall" bold
                  :loading="store.resume.uploading"
                  :disabled="startDisabled">
        Start Auto-Complete →
      </FormButton>
    </div>

  </div>
</template>

<script setup>
import BorderGradient from "./BorderGradient.vue";
import FormButton from "./FormButton.vue";
import FormCheckbox from "./FormCheckbox.vue";
import HoverTooltip from "./HoverTooltip.vue";
import UploadResume from "./UploadResume.vue";
import {ArrowsRightLeftIcon, DocumentIcon} from "@heroicons/vue/24/solid";
import {useParseResumeStore} from "../stores/ParseResumeStore.js";
import {computed, ref} from "vue";
import {useAccountStore} from "../stores/AccountStore.js";

const props = defineProps({
  hideTermsOfService: Boolean,
  backgroundHex: String,
  foregroundHex: String,
});

const store = useParseResumeStore();
const accountStore = useAccountStore();

const checkboxContainer = ref(null);
const fileName = ref("");
const formattedFileSize = ref("0kb");

const focusCheckboxTimeout = () => {
  setTimeout(() => {
    checkboxContainer.value?.checkbox?.focus();
  }, 500);
};

const hasFile = computed(() => !!store.resume.file);

const showTermsOfService = computed(() => !accountStore.isLoggedIn && store.resume.file && !props.hideTermsOfService);

const startDisabled = computed(() => !store.resume.file || (!store.resume.tosChecked && !accountStore.isLoggedIn && !props.hideTermsOfService));
</script>
